import { ErrorMessage } from '@hookform/error-message'
import { useFormContext } from 'react-hook-form'
import { useRef, useEffect, useState, useContext } from 'react'
import { useTranslation } from 'next-i18next'
import PropTypes from 'prop-types'

// Internal components
import FormError from '../FormError/FormError'
import Svg from '../Svg/Svg'
import { UserContext } from '../../context/UserContext'

const revealClass = ' is-revealed'
const activeClass = ' is-active'

const requiredHint = ' *'

const FormItem = (props) => {
  const { item, onFormItemLoad, groupedState, revealState } = props
  const { register, watch, errors, getValues, setValue, formState } = useFormContext()
  const userContext = useContext(UserContext)

  // Translations
  const { t } = useTranslation(
    userContext?.userSession?.sportType?.slug ??
      process.env.NEXT_PUBLIC_RINUS_DEFAULT_NAMESPACE ??
      process.env.NEXT_PUBLIC_RINUS_DEFAULT_SPORT
  )

  const requiredErrorMessage = t('FORMITEM__ERROR_REQUIRED')

  const [isRequired, setIsRequired] = useState({ required: false })
  const [isGrouped, setIsGrouped] = useState(false)
  const [isLoaded, setIsLoaded] = useState(false)
  const [cachedValue, setCachedValue] = useState()

  const itemProps = item
  const itemRef = useRef()

  const itemValue = watch(item.name)
  const itemInputRef = register(item.name, isRequired)
  const itemCheckboxRef = item?.checkbox ? register(item.checkbox.name, isRequired) : false

  useEffect(() => {
    if (!itemValue || isLoaded) return false
    onFormItemLoad({ itemProps, itemRef, itemValue })
    setIsLoaded(true)
  }, [itemValue])

  useEffect(() => {
    if (!item.revealed && item.required) setIsRequired({ required: requiredErrorMessage })
  }, [])

  useEffect(() => {
    if (item.revealed && item.required) setIsRequired({ required: revealState ? requiredErrorMessage : false })
  }, [revealState])

  useEffect(() => {
    if (item?.group && groupedState) {
      const itemLabel = item?.group?.label
      const groupedItem = groupedState.filter((str) => {
        return str.label.indexOf(itemLabel) >= 0
      })
      setIsGrouped(groupedItem[0].active)
    }
  }, [groupedState])

  const onInputChange = (e) => {
    triggerInputChange(e.target, true)
  }

  const onCheckboxChange = (e) => {
    const input = itemRef.current.querySelector('.FormItem-input')
    const value = getValues(item.name).toString()
    if (value !== 0 && e.target.checked) setValue(item.name, 0)
    if (e.target.checked === false) {
      if (parseInt(value) === 0) setValue(item.name, cachedValue >= 1 ? cachedValue : 0)
      input.focus()
    }
  }

  const decrementFormItem = () => {
    const input = itemRef.current.querySelector('.FormItem-input')
    const min = input.dataset.min
    let value = getValues(item.name)
    value--
    if (!min || value >= min) {
      setValue(item.name, value)
      triggerInputChange(input, true)
    }
  }

  const incrementFormItem = () => {
    const input = itemRef.current.querySelector('.FormItem-input')
    const max = input.dataset.max
    let value = getValues(item.name)
    value++
    if (!max || value <= max) {
      setValue(item.name, value)
      triggerInputChange(input, true)
    }
  }

  const triggerInputChange = (input, trigger) => {
    const validatedValue = validateInput(input)
    setValue(item.name, validatedValue !== 0 ? validatedValue : trigger === true ? '' : 0)
    setCachedValue(input.value)
    if (item.checkbox) setValue(item.checkbox.name, validatedValue === 0)
  }

  const validateInput = (input) => {
    const max = input.dataset.max
    const value = getValues(item.name).toString()
    if (value && parseInt(value) !== 0 && parseInt(value) <= max) {
      if ((value.match(/^(0?[1-9]|[1-9][0-9])$/) && parseInt(value) > 0) !== true) {
        return cachedValue
      } else {
        return parseInt(value)
      }
    } else return 0
  }

  return (
    <>
      <div
        ref={itemRef}
        className={`FormItem FormItem--${item.type}${item.modifier ? ` FormItem--${item.modifier}` : ''}${
          revealState ? revealClass : ''
        }${!isGrouped ? '' : activeClass}`}
        data-reveal-target={item.revealed}
      >
        <button
          className="FormItem--numberButton"
          type="button"
          tabIndex="-1"
          onClick={(e) => decrementFormItem(e)}
        ></button>
        {item.label && <div className={'FormItem--numberLabel'}>{item.label}</div>}
        <input
          type="number"
          placeholder="0"
          pattern="[0-9]*"
          data-min="0"
          data-max="99"
          defaultValue={item.value}
          name={item.name}
          id={item.name}
          className={`FormItem-input FormItem-input--${item.type}`}
          onChange={(e) => {
            itemInputRef.onChange(e)
            onInputChange(e)
          }}
          ref={itemInputRef.ref}
        />
        <button
          className="FormItem--numberButton"
          type="button"
          tabIndex="-1"
          onClick={(e) => incrementFormItem(e)}
        ></button>
      </div>
      {item.checkbox && (
        <div
          className={`FormItem FormItem--${item.checkbox.type}${
            item.checkbox.modifier ? ` FormItem--${item.checkbox.modifier}` : ''
          }${revealState ? revealClass : ''}${!isGrouped ? '' : activeClass}`}
          data-reveal-target={item.checkbox.revealed}
        >
          <label className="FormItem-checkboxLabel" htmlFor={item.checkbox.name}>
            <input
              type="checkbox"
              defaultChecked={item.checkbox.checked}
              id={item.checkbox.name}
              name={item.checkbox.name}
              className={`FormItem-input FormItem-input--${item.checkbox.type}`}
              onChange={(e) => {
                itemCheckboxRef.onChange(e)
                onCheckboxChange(e)
              }}
              ref={itemCheckboxRef.ref}
            />
            <div className="FormItem-checkboxIndicator">
              <Svg id="icon-check" modifiers={['FormItem-checkboxIcon']} />
            </div>
            {`${item.checkbox.transl ? t(item.checkbox.transl) : item.checkbox.label}${
              item.checkbox.required ? requiredHint : ''
            }`}
            <ErrorMessage errors={errors} name={item.name} as={<FormError />} />
          </label>
        </div>
      )}
      {formState.isSubmitted && <ErrorMessage errors={errors} name={item.name} as={<FormError />} />}
    </>
  )
}

FormItem.propTypes = {
  item: PropTypes.object,
  onFormItemLoad: PropTypes.func,
  revealState: PropTypes.any,
  groupedState: PropTypes.any
}

export default FormItem
